import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Form } from '@unform/web';
import { FormHandles } from '@unform/core';
import * as Yup from 'yup';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { FiArrowLeft, FiLock, FiMail, FiPhone } from 'react-icons/fi';

import api from '../../services/api';
import { useToast } from '../../hooks/toast';
import getValidationErrors from '../../utils/getValidationErrors';

import signUpBackgroundImg from '../../assets/images/sign-up-background.png';
import Input from '../../components/Input';
import Button from '../../components/Button';
import AuthLayout from '../../components/layouts/AuthLayout';

interface FormData {
  phone: string;
  email: string;
  password: string;
  confirmPassword: string;
}

type ClaimPrefillState = Partial<Pick<FormData, 'phone' | 'email'>>;

const ClaimAccount: React.FC = () => {
  const formRef = useRef<FormHandles>(null);
  const { addToast } = useToast();
  const history = useHistory();
  const location = useLocation<ClaimPrefillState | undefined>();
  const [pending, setPending] = useState<null | { phone: string; email: string; password: string }>(null);
  const [code, setCode] = useState<string[]>(['', '', '', '', '', '']);
  const [submitting, setSubmitting] = useState(false);

  const initialData = useMemo(() => {
    const st = location.state || {};
    return {
      phone: st.phone ?? '',
      email: st.email ?? '',
    };
  }, [location.state]);

  const onSubmit = useCallback(
    async (data: FormData) => {
      try {
        const r = formRef.current;
        if (r) r.setErrors({});
        const schema = Yup.object().shape({
          phone: Yup.string().min(8).max(32).required('Telemóvel obrigatório'),
          email: Yup.string().email('E-mail inválido').required('E-mail obrigatório'),
          password: Yup.string()
            .min(6, 'A palavra-passe deve ter pelo menos 6 caracteres')
            .required('Palavra-passe obrigatória'),
          confirmPassword: Yup.string()
            .oneOf([Yup.ref('password')], 'Não coincide com a palavra-passe')
            .required('Confirmação obrigatória'),
        });
        await schema.validate(data, { abortEarly: false });

        setSubmitting(true);

        const resp = await api.post<{ ok: boolean; eligible?: boolean; debug_code?: string }>(
          '/users/claim-request',
          {
            phone: data.phone,
            email: data.email,
          },
        );

        if (!resp.data?.eligible) {
          addToast({
            type: 'error',
            title: 'Não encontrado',
            description:
              'Não encontrámos este registo de cliente (ou a conta já existe). Confirma o telemóvel ou pede ao salão para te registarem primeiro.',
          });
          return;
        }

        setPending({ phone: data.phone, email: data.email, password: data.password });
        setCode(['', '', '', '', '', '']);
        addToast({
          type: 'success',
          title: 'Código enviado',
          description: 'Enviámos um código de verificação para o teu e-mail.',
        });
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const r = formRef.current;
          if (r) r.setErrors(getValidationErrors(err));
          return;
        }
        addToast({
          type: 'error',
          title: 'Erro',
          description: (err as any)?.response?.data?.error ?? 'Não foi possível enviar o código. Tenta novamente.',
        });
      } finally {
        setSubmitting(false);
      }
    },
    [addToast],
  );

  const submitCode = useCallback(async () => {
    if (!pending) return;
    const codeStr = code.join('');
    if (!/^\d{6}$/.test(codeStr)) {
      addToast({ type: 'error', title: 'Código inválido', description: 'Indica os 6 dígitos.' });
      return;
    }
    try {
      setSubmitting(true);
      await api.post('/users/claim', {
        phone: pending.phone,
        email: pending.email,
        code: codeStr,
        password: pending.password,
      });
      addToast({ type: 'success', title: 'Conta criada', description: 'Já podes iniciar sessão.' });
      history.push('/');
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Erro',
        description: (err as any)?.response?.data?.error ?? 'Não foi possível confirmar o código.',
      });
    } finally {
      setSubmitting(false);
    }
  }, [addToast, code, history, pending]);

  return (
    <AuthLayout
      title="Concluir registo"
      backgroundImage={signUpBackgroundImg}
      animationFrom="right"
      authLogo="signup"
    >
      <Form ref={formRef} onSubmit={onSubmit} initialData={initialData}>
        <h1>Concluir registo</h1>

        <Input name="phone" icon={FiPhone} placeholder="Telemóvel" />
        <Input
          name="email"
          icon={FiMail}
          type="email"
          inputMode="email"
          autoComplete="email"
          placeholder="E-mail"
        />
        <Input
          name="password"
          icon={FiLock}
          type="password"
          placeholder="Nova palavra-passe"
        />
        <Input
          name="confirmPassword"
          icon={FiLock}
          type="password"
          placeholder="Confirmar palavra-passe"
        />
        <Button type="submit" disabled={submitting}>
          {submitting ? 'A enviar…' : 'Enviar código'}
        </Button>
      </Form>

      <Link to="/">
        <FiArrowLeft />
        Voltar ao início de sessão
      </Link>

      {!!pending && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-[999] flex items-center justify-center bg-black/40 p-6 backdrop-blur-sm"
          onClick={() => !submitting && setPending(null)}
        >
          <div
            className="w-full max-w-[420px] rounded-[14px] border border-[var(--color-input-border)] bg-[var(--color-white)] p-5 shadow-lg"
            onClick={e => e.stopPropagation()}
          >
            <h2 className="mb-2 text-[var(--color-text-white)]">Confirmar código</h2>
            <p className="mb-3.5 text-sm leading-snug text-[var(--color-light-gray)]">
              Introduz os seis dígitos que enviámos para o teu e-mail e clica em «Criar conta».
            </p>

            <div className="my-3.5 flex justify-center gap-2.5">
              {code.map((v, idx) => (
                <input
                  key={String(idx)}
                  className="h-[54px] w-[46px] rounded-xl border border-[var(--color-input-border)] bg-[var(--color-inputs)] text-center text-[22px] font-extrabold text-[var(--color-text-white)]"
                  inputMode="numeric"
                  pattern="\d*"
                  maxLength={1}
                  value={v}
                  onChange={e => {
                    const raw = e.target.value.replace(/\D/g, '').slice(-1);
                    setCode(prev => {
                      const next = prev.slice();
                      next[idx] = raw;
                      return next;
                    });
                    const nextEl = (e.target as HTMLInputElement).nextElementSibling as HTMLInputElement | null;
                    if (raw && nextEl) nextEl.focus();
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Backspace' && !code[idx]) {
                      const prevEl = (e.currentTarget as HTMLInputElement).previousElementSibling as HTMLInputElement | null;
                      if (prevEl) prevEl.focus();
                    }
                  }}
                />
              ))}
            </div>

            <div className="flex flex-wrap gap-2.5">
              <Button type="button" onClick={submitCode} disabled={submitting}>
                {submitting ? 'A confirmar…' : 'Criar conta'}
              </Button>
              <Button type="button" onClick={() => setPending(null)} disabled={submitting}>
                Cancelar
              </Button>
            </div>
          </div>
        </div>
      )}
    </AuthLayout>
  );
};

export default ClaimAccount;
